import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useAtomValue } from "jotai";
import { authAtom } from "../../stores/auth";
import { localeAtom } from "../../stores/locale";
import { invitationApi } from "../../api/client";
import { t } from "../../lib/i18n";
import { toUserMessage } from "../../lib/errorMessage";
import CosmicButton from "../../components/CosmicButton";

export default function InviteAccept() {
  const { code } = useParams<{ code: string }>();
  const auth = useAtomValue(authAtom);
  const locale = useAtomValue(localeAtom);
  const navigate = useNavigate();

  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!code) {
      setStatus("error");
      setError(t("errors.invite.noCode", locale));
      return;
    }
    if (!auth.isAuthenticated) {
      // Store invite code and redirect to login
      localStorage.setItem("pending_invite", code);
      navigate("/login");
      return;
    }

    invitationApi
      .accept(code)
      .then(() => {
        setStatus("success");
        localStorage.removeItem("pending_invite");
      })
      .catch((err) => {
        console.error("invite accept failed", err);
        setStatus("error");
        setError(toUserMessage(err, locale, "errors.invite.accept"));
      });
  }, [code, auth.isAuthenticated, navigate, locale]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gruvbox-bg p-lg">
      <div className="w-full max-w-[28rem] rounded-xl border border-gruvbox-bg2 bg-gruvbox-bg1 p-2xl text-center">
        {status === "loading" && (
          <>
            <div className="text-4xl animate-pulse">🔭</div>
            <p className="mt-lg text-sm text-gruvbox-fg1">
              {t("invite.processing", locale)}
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="text-4xl">✓</div>
            <h2 className="mt-lg text-xl font-bold text-gruvbox-fg">
              {t("invite.joined", locale)}
            </h2>
            <p className="mt-md text-sm text-gruvbox-fg1">
              {t("invite.joinedBody", locale)}
            </p>
            <CosmicButton
              onClick={() => navigate("/")}
              variant="primary"
              size="md"
              className="mt-2xl w-full"
            >
              {t("invite.openObservatory", locale)}
            </CosmicButton>
          </>
        )}

        {status === "error" && (
          <>
            <div className="text-4xl">✕</div>
            <h2 className="mt-lg text-xl font-bold text-gruvbox-red">
              {t("invite.error", locale)}
            </h2>
            <p className="mt-md text-sm text-gruvbox-fg1">{error}</p>
            <CosmicButton
              onClick={() => navigate("/")}
              variant="primary"
              size="md"
              className="mt-2xl w-full"
            >
              {t("invite.goHome", locale)}
            </CosmicButton>
          </>
        )}
      </div>
    </div>
  );
}
